import { useCallback, useEffect, useRef, useState } from "react";
import type { LatLon, WorldSnapshot } from "@radar-sim/schema";
import type { AreaData } from "@radar-sim/area-data";
import { projectPosition as projectFromOwn } from "@radar-sim/engine";
import type { RadarStateSource } from "@radar-sim/engine";
import { drawPPI, fnv32, type EchoTrailSample } from "./render/draw.js";
import {
  latLonToScreen,
  screenToBearingRange,
  type PPIViewport,
} from "./coords.js";
import type { DisplayState } from "./displayState.js";
import { MarpaTracker } from "./marpa.js";
import { describeLight } from "@radar-sim/area-data";

/**
 * Own-ship trail capacity. At 0.5 sim-second sample period this gives
 * 30 sim-seconds of wake history.
 */
const TRAIL_CAPACITY = 60;

/** Per-target echo-trail capacity (samples × period = sim-seconds shown). */
const ECHO_CAPACITY = 60;
const ECHO_SAMPLE_PERIOD_SECONDS = 1;

export interface PPIProps {
  source: RadarStateSource;
  area: AreaData | undefined;
  scenarioId: string;
  display: DisplayState;
  onDisplayChange: (next: DisplayState) => void;
  /**
   * MARPA tracker. Optional — when supplied (solo simulator mode), the
   * AIS readout panel can subscribe to the same instance. Classroom
   * and debrief views don't surface a per-client AIS readout, so they
   * omit it and let PPI manage one internally.
   */
  marpa?: MarpaTracker;
  /**
   * When supplied, the next radar tap resolves a pending vessel
   * spawn at the tapped (bearing, range) → world point instead of
   * acquiring a target. Driven by the AddVesselPicker's
   * click-to-place mode at the SessionRunner level.
   */
  onPendingSpawnAt?: (at: LatLon) => void;
  /**
   * Optional fixed size in CSS pixels. When omitted (default), the PPI
   * fills its parent container and tracks resize via ResizeObserver —
   * the canvas always renders as a square sized to the smaller of the
   * parent's width or height.
   */
  size?: number;
}

const FALLBACK_SIZE = 480;

/**
 * Plan-Position Indicator (PPI) — the actual radar display. Subscribes to
 * the source's snapshot stream, manages per-client display state, and
 * draws to a `<canvas>` on every animation frame.
 */
export function PPI(props: PPIProps): JSX.Element {
  const { source, area, scenarioId, display, onDisplayChange } = props;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const snapshotRef = useRef<WorldSnapshot | null>(null);
  const sweepRef = useRef(0);
  const marpaRef = useRef<MarpaTracker>(props.marpa ?? new MarpaTracker());
  // If the parent supplies a tracker, adopt it (so the AIS panel and
  // the PPI share state). Otherwise we fall back to the local
  // instance created above and keep using it across renders.
  if (props.marpa !== undefined) marpaRef.current = props.marpa;
  const seedRef = useRef(fnv32(scenarioId));
  const trailRef = useRef<LatLon[]>([]);
  const lastTrailSampleRef = useRef<number>(-Infinity);
  const echoTrailsRef = useRef<Map<string, EchoTrailSample[]>>(new Map());
  const lastEchoSampleRef = useRef<number>(-Infinity);
  const [size, setSize] = useState<number>(props.size ?? FALLBACK_SIZE);
  const [selectedMarkId, setSelectedMarkId] = useState<string | null>(null);
  const [markScreen, setMarkScreen] = useState<{ x: number; y: number } | null>(
    null,
  );

  // Track the wrapper's bounding box and resize the canvas to fit. The
  // canvas always renders as a square sized to the smaller of the
  // wrapper's width or height, so the PPI scales with viewport changes
  // without overflowing its grid cell.
  useEffect(() => {
    if (props.size !== undefined) return;
    const wrapper = wrapperRef.current;
    if (wrapper === null) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const next = Math.max(160, Math.floor(Math.min(width, height)));
        setSize((prev) => (prev === next ? prev : next));
      }
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [props.size]);

  // Keep the seed in sync with the scenario id (handles scenario
  // hot-swap). When the parent owns the tracker (solo simulator) it
  // hands us a fresh instance per scenario; when we own it (classroom,
  // debrief) we reset it ourselves.
  useEffect(() => {
    seedRef.current = fnv32(scenarioId);
    if (props.marpa === undefined) {
      marpaRef.current = new MarpaTracker();
    }
    trailRef.current = [];
    lastTrailSampleRef.current = -Infinity;
    echoTrailsRef.current = new Map();
    lastEchoSampleRef.current = -Infinity;
  }, [scenarioId, props.marpa]);

  // Recompute the popover anchor when the mark moves on screen
  // (own ship motion / range change). Throttled to snapshot rate
  // (~10Hz) so the popover follows the mark smoothly without
  // re-rendering on every animation frame.
  useEffect(() => {
    if (selectedMarkId === null) return;
    if (area === undefined) return;
    const mark = area.marks.find((m) => m.id === selectedMarkId);
    if (mark === undefined) {
      setMarkScreen(null);
      return;
    }
    const canvas = canvasRef.current;
    const snap = snapshotRef.current;
    if (canvas === null || snap === null) return;
    const radiusPx = Math.min(canvas.width, canvas.height) / 2 - 24;
    const viewport: PPIViewport = {
      cx: canvas.width / 2,
      cy: canvas.height / 2,
      radiusPx,
      rangeNm: display.rangeNm,
      mode: display.mode,
      ownCog: snap.ownShip.cog,
    };
    const xy = latLonToScreen(snap.ownShip.position, mark.position, viewport);
    // Convert from canvas-pixel space (DPR-scaled) back to CSS pixels
    // for HTML positioning.
    const dpr = window.devicePixelRatio || 1;
    setMarkScreen({ x: xy.x / dpr, y: xy.y / dpr });
  }, [selectedMarkId, area, display, size]);

  // Subscribe to source snapshots.
  useEffect(() => {
    const unsubscribe = source.subscribe((snap) => {
      snapshotRef.current = snap;
      marpaRef.current.update(snap.simTimeSeconds, snap.ownShip, snap.targets);
      // If a mark is selected, refresh its on-screen anchor each tick
      // so the popover follows own-ship motion.
      if (selectedMarkId !== null && area !== undefined) {
        const mark = area.marks.find((m) => m.id === selectedMarkId);
        const canvas = canvasRef.current;
        if (mark !== undefined && canvas !== null) {
          const radiusPx = Math.min(canvas.width, canvas.height) / 2 - 24;
          const viewport: PPIViewport = {
            cx: canvas.width / 2,
            cy: canvas.height / 2,
            radiusPx,
            rangeNm: display.rangeNm,
            mode: display.mode,
            ownCog: snap.ownShip.cog,
          };
          const xy = latLonToScreen(
            snap.ownShip.position,
            mark.position,
            viewport,
          );
          const dpr = window.devicePixelRatio || 1;
          setMarkScreen({ x: xy.x / dpr, y: xy.y / dpr });
        }
      }
      // Sample the own-ship position every 0.5 sim seconds. The trail
      // ring is bounded; oldest entry drops off when the buffer is full.
      if (snap.simTimeSeconds - lastTrailSampleRef.current >= 0.5) {
        const buf = trailRef.current;
        buf.push({ ...snap.ownShip.position });
        if (buf.length > TRAIL_CAPACITY) {
          buf.splice(0, buf.length - TRAIL_CAPACITY);
        }
        lastTrailSampleRef.current = snap.simTimeSeconds;
      }
      // Sample each target's position once per ECHO_SAMPLE_PERIOD_SECONDS.
      // Builds up a per-target ring buffer that drawEchoTrails reads.
      if (
        snap.simTimeSeconds - lastEchoSampleRef.current >=
        ECHO_SAMPLE_PERIOD_SECONDS
      ) {
        const live = new Set<string>();
        for (const t of snap.targets) {
          live.add(t.id);
          let samples = echoTrailsRef.current.get(t.id);
          if (samples === undefined) {
            samples = [];
            echoTrailsRef.current.set(t.id, samples);
          }
          samples.push({
            position: { ...t.position },
            simSeconds: snap.simTimeSeconds,
          });
          if (samples.length > ECHO_CAPACITY) {
            samples.splice(0, samples.length - ECHO_CAPACITY);
          }
        }
        // Drop trails for vessels that have left the world.
        for (const id of [...echoTrailsRef.current.keys()]) {
          if (!live.has(id)) echoTrailsRef.current.delete(id);
        }
        lastEchoSampleRef.current = snap.simTimeSeconds;
      }
    });
    return unsubscribe;
  }, [source]);

  // Animation loop.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const canvas = canvasRef.current;
      const snapshot = snapshotRef.current;
      if (canvas !== null && snapshot !== null) {
        const ctx = canvas.getContext("2d");
        if (ctx !== null) {
          sweepRef.current = (sweepRef.current + 2.5) % 360;
          drawPPI({
            ctx,
            width: canvas.width,
            height: canvas.height,
            snapshot,
            display,
            area,
            marpa: marpaRef.current.list(),
            sweepDeg: sweepRef.current,
            scenarioSeed: seedRef.current,
            ownShipTrail: trailRef.current,
            echoTrails: echoTrailsRef.current,
          });
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [display, area]);

  // Handle taps: tap on a target ⇒ acquire MARPA. Tap on EBL/VRM handle ⇒ drag.
  const handleTap = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const snap = snapshotRef.current;
      if (canvas === null || snap === null) return;
      const rect = canvas.getBoundingClientRect();
      const x = ((e.clientX - rect.left) * canvas.width) / rect.width;
      const y = ((e.clientY - rect.top) * canvas.height) / rect.height;
      const radiusPx = Math.min(canvas.width, canvas.height) / 2 - 24;
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const viewport: PPIViewport = {
        cx,
        cy,
        radiusPx,
        rangeNm: display.rangeNm,
        mode: display.mode,
        ownCog: snap.ownShip.cog,
      };
      // Pending vessel-spawn from the AddVesselPicker (click-to-place
      // mode) — convert tap to world point, fulfil, skip everything
      // else.
      if (props.onPendingSpawnAt !== undefined) {
        const br = screenToBearingRange({ x, y }, viewport);
        const at = projectFromOwn(snap.ownShip.position, br.trueBearing, br.rangeNm);
        props.onPendingSpawnAt(at);
        return;
      }
      // Hit-test targets: closest within 16 px.
      let bestId: string | undefined;
      let bestDist = 16;
      for (const t of snap.targets) {
        const xy = latLonToScreen(snap.ownShip.position, t.position, viewport);
        const d = Math.hypot(xy.x - x, xy.y - y);
        if (d < bestDist) {
          bestDist = d;
          bestId = t.id;
        }
      }
      if (bestId !== undefined) {
        if (marpaRef.current.isAcquired(bestId)) {
          marpaRef.current.drop(bestId);
        } else {
          marpaRef.current.acquire(bestId, snap.simTimeSeconds);
          void source.sendCommand({
            kind: "acquire_target",
            vesselId: bestId,
          });
        }
        // Tapping a vessel also dismisses any open mark popover so we
        // don't end up with overlapping selections.
        setSelectedMarkId(null);
        return;
      }
      // No target hit — try marks (buoys, towers, lights, RACONs).
      // Wider hit threshold than vessels because marks paint as small
      // 3-5 px dots and a finger / cursor needs slack.
      if (area !== undefined) {
        let bestMark: string | undefined;
        let bestMarkDist = 18;
        for (const m of area.marks) {
          const xy = latLonToScreen(snap.ownShip.position, m.position, viewport);
          const d = Math.hypot(xy.x - x, xy.y - y);
          if (d < bestMarkDist) {
            bestMarkDist = d;
            bestMark = m.id;
          }
        }
        if (bestMark !== undefined) {
          // Toggle: tap an already-selected mark to dismiss.
          setSelectedMarkId((prev) => (prev === bestMark ? null : bestMark!));
          return;
        }
      }
      // Empty water — clear any open popover and set EBL 1 to the
      // tapped bearing as a quick interaction.
      setSelectedMarkId(null);
      const br = screenToBearingRange({ x, y }, viewport);
      const next: DisplayState = {
        ...display,
        ebl: display.ebl.map((e) =>
          e.id === 1 ? { ...e, bearing: br.trueBearing, visible: true } : e,
        ),
        vrm: display.vrm.map((v) =>
          v.id === 1 ? { ...v, rangeNm: br.rangeNm, visible: true } : v,
        ),
      };
      onDisplayChange(next);
    },
    [display, onDisplayChange, source, area, props.onPendingSpawnAt],
  );

  const selectedMark =
    selectedMarkId !== null && area !== undefined
      ? area.marks.find((m) => m.id === selectedMarkId)
      : undefined;

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
        }}
      >
        <canvas
          ref={canvasRef}
          width={size}
          height={size}
          onPointerDown={handleTap}
          role="img"
          aria-label={`Radar plan position indicator. Range ${display.rangeNm} nm, mode ${display.mode}.`}
          data-radar-ppi="true"
          tabIndex={0}
          style={{
            width: size,
            height: size,
            background: "#02100b",
            borderRadius: "50%",
            touchAction: "none",
          }}
        />
        {selectedMark !== undefined && markScreen !== null && (
          <MarkPopover
            mark={selectedMark}
            x={markScreen.x}
            y={markScreen.y}
            containerSize={size}
            onClose={() => setSelectedMarkId(null)}
          />
        )}
      </div>
    </div>
  );
}

function MarkPopover(props: {
  mark: import("@radar-sim/area-data").Mark;
  x: number;
  y: number;
  containerSize: number;
  onClose: () => void;
}): JSX.Element {
  const { mark, x, y, containerSize } = props;
  // Anchor the popover to the upper-right of the mark by default;
  // flip to upper-left if it would overflow the canvas right edge.
  const POPOVER_W = 200;
  const flip = x + 24 + POPOVER_W > containerSize;
  const left = flip ? Math.max(8, x - 24 - POPOVER_W) : Math.min(containerSize - POPOVER_W - 8, x + 24);
  const top = Math.max(8, Math.min(containerSize - 110, y - 60));
  const morse = mark.racon !== undefined ? toMorseDots(mark.racon.code) : undefined;
  return (
    <div
      role="dialog"
      aria-label={`${mark.name} details`}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left,
        top,
        width: POPOVER_W,
        background: "rgba(11, 18, 32, 0.92)",
        border: "1px solid rgba(255, 220, 130, 0.6)",
        borderRadius: 6,
        padding: "10px 12px",
        color: "#f3f4f6",
        fontSize: 12,
        fontFamily: "ui-sans-serif, system-ui",
        boxShadow: "0 6px 16px rgba(0,0,0,0.5)",
        pointerEvents: "auto",
        backdropFilter: "blur(4px)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <strong style={{ fontSize: 13, color: "#fbbf24", lineHeight: 1.2 }}>
          {mark.name}
        </strong>
        <button
          onClick={props.onClose}
          aria-label="Close"
          style={{
            background: "transparent",
            border: "none",
            color: "#9aa6b8",
            fontSize: 14,
            lineHeight: 1,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <div style={{ marginTop: 6, fontFamily: "ui-monospace, monospace", color: "#9aa6b8", fontSize: 11 }}>
        Type · {mark.type}
        {mark.category !== undefined ? ` · ${describeCategory(mark.category)}` : ""}
      </div>
      <div style={{ fontFamily: "ui-monospace, monospace", color: "#9aa6b8", fontSize: 11 }}>
        {mark.position.lat.toFixed(4)}°N, {Math.abs(mark.position.lon).toFixed(4)}°W
      </div>
      {mark.racon !== undefined && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid rgba(255, 220, 130, 0.25)",
            fontSize: 11,
            lineHeight: 1.4,
          }}
        >
          <span style={{ color: "#fbbf24", fontWeight: 600 }}>RACON</span>
          <div style={{ fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
            {mark.racon.code} · {morse ?? "—"}
          </div>
        </div>
      )}
      {mark.light !== undefined && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: "1px solid rgba(255, 220, 130, 0.25)",
            fontSize: 11,
            lineHeight: 1.4,
          }}
        >
          <span style={{ color: "#fbbf24", fontWeight: 600 }}>LIGHT</span>
          <div style={{ fontFamily: "ui-monospace, monospace", marginTop: 2 }}>
            {mark.light.character}
            {mark.light.rangeNm !== undefined ? ` · ${mark.light.rangeNm}M` : ""}
            {mark.light.heightM !== undefined ? ` · ${mark.light.heightM}m` : ""}
          </div>
          <div style={{ marginTop: 2, color: "#9aa6b8" }}>
            {describeLight(mark.light)}
          </div>
          {mark.light.fogSignal !== undefined && (
            <div style={{ marginTop: 6 }}>
              <span style={{ color: "#fbbf24", fontWeight: 600 }}>FOG</span>
              <div
                style={{ fontFamily: "ui-monospace, monospace", marginTop: 2 }}
              >
                {mark.light.fogSignal}
              </div>
              <div style={{ marginTop: 2, color: "#9aa6b8" }}>
                {describeMarkFogSignal(mark.light.fogSignal)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Translate the chart-style fog-signal abbreviation on a mark into a
 * line of plain English. Handles the common idioms; falls back to
 * echoing the raw abbreviation for anything exotic.
 */
function describeMarkFogSignal(sig: string): string {
  // "Horn(N)Ts" → N horn blasts every Ts seconds
  let m = /^Horn\((\d+)\)(\d+)s$/i.exec(sig);
  if (m !== null) {
    return `${m[1]} horn blasts every ${m[2]} seconds.`;
  }
  // "Bell(N)Ts" — bell rung N times every Ts seconds
  m = /^Bell\((\d+)\)(\d+)s$/i.exec(sig);
  if (m !== null) {
    return `Bell rung ${m[1]}× at intervals of ${m[2]} seconds.`;
  }
  if (/^bell$/i.test(sig)) return "Bell, manually struck.";
  if (/^whis/i.test(sig))
    return "Whistle — wave-actuated, sounds with the swell.";
  if (/^gong$/i.test(sig)) return "Gong (paired with bell on >100m anchor lights).";
  // Mo(L)Ts → Morse "L" sounded every Ts
  m = /^Mo\(([A-Z])\)(\d+)s$/i.exec(sig);
  if (m !== null) {
    return `Morse "${m[1]?.toUpperCase()}" sounded every ${m[2]} seconds.`;
  }
  return sig;
}

function describeCategory(
  c: import("@radar-sim/area-data").BuoyCategory,
): string {
  switch (c) {
    case "port-hand":
      return "port-hand lateral (red can, Fl.R)";
    case "starboard-hand":
      return "starboard-hand lateral (green cone, Fl.G)";
    case "north-cardinal":
      return "north cardinal (Q.W, pass to the north)";
    case "east-cardinal":
      return "east cardinal (Q(3) W, pass to the east)";
    case "south-cardinal":
      return "south cardinal (Q(6)+LFl W, pass to the south)";
    case "west-cardinal":
      return "west cardinal (Q(9) W, pass to the west)";
    case "safe-water":
      return "safe-water mark (Iso/Oc W or LFl)";
    case "isolated-danger":
      return "isolated danger (Fl(2) W)";
    case "special":
      return "special mark (Fl.Y)";
    case "preferred-port":
      return "preferred channel to starboard (red with green band)";
    case "preferred-starboard":
      return "preferred channel to port (green with red band)";
  }
}

function toMorseDots(char: string): string | undefined {
  // Dynamic import to avoid pulling the table into every render — cheap
  // since it's the same module already loaded by the renderer.
  const tbl: Record<string, string> = {
    A: "·–", B: "–···", C: "–·–·", D: "–··", E: "·",
    F: "··–·", G: "––·", H: "····", I: "··", J: "·–––",
    K: "–·–", L: "·–··", M: "––", N: "–·", O: "–––",
    P: "·––·", Q: "––·–", R: "·–·", S: "···", T: "–",
    U: "··–", V: "···–", W: "·––", X: "–··–", Y: "–·––", Z: "––··",
  };
  return tbl[char.toUpperCase()];
}

export { DEFAULT_DISPLAY_STATE } from "./displayState.js";
export type { DisplayState } from "./displayState.js";
